// src/pages/batch/hooks/useBatchData.js
import { useState, useCallback, useEffect } from "react";
import { message as antdMessage } from "antd";
import { postRequest } from "../../../../util/service";
import { SERVER_FILTER_THRESHOLD } from "../../../../util/helper";
import { sanitizeRemovedJobResultRow } from "../../../../util/removedJobGuards";

const isEmptyFilters = (filters) => {
  if (!filters) return true;
  if (Array.isArray(filters)) return filters.length === 0;
  if (typeof filters === "object") return Object.keys(filters).length === 0;
  return true;
};

export const useBatchData = (projectId, batchId) => {
  const [historyData, setHistoryData] = useState([]);
  const [chunkStart, setChunkStart] = useState(0);
  const [nextChunkStart, setNextChunkStart] = useState(0);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [loading, setLoading] = useState(false);
  const [backendMessage, setBackendMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [frontPage, setFrontPage] = useState(1);
  const [requiresServerFiltering, setRequiresServerFiltering] = useState(false);
  const [lastFiltersKey, setLastFiltersKey] = useState("[]");

  const fetchChunk = useCallback(
    async (opts = { start: 0, filters: {} }) => {
      if (!projectId || !batchId) return;
      const { start, filters = {} } = opts;
      const filtersKey = JSON.stringify(filters ?? {});
      setLastFiltersKey(filtersKey);
      setLoading(true);
      setBackendMessage("");
      setStatusMessage("");

      try {
        const payload = { projectId, batchId, start_line: start, filters };
        const { data } = await postRequest(
          `/riva/moleculegenbatches/molecules/result`,
          payload
        );

        if (typeof data === "string") {
          setHistoryData([]);
          setBackendMessage(data);
          setTotalFiltered(0);
        } else {
          const mols = (data.molecules || []).map(sanitizeRemovedJobResultRow);
          setHistoryData(mols);
          setChunkStart(data.current_start_line ?? start);
          setNextChunkStart(data.next_start_line ?? start + mols.length);
          setTotalFiltered(data.filtered_count ?? mols.length);
          setStatusMessage(data.status_message || "");
          if (isEmptyFilters(filters)) {
            setRequiresServerFiltering((prev) =>
              prev || (data.filtered_count ?? mols.length) > SERVER_FILTER_THRESHOLD
            );
          }
        }
        setFrontPage(1); // Reset frontend pagination on new chunk
      } catch (err) {
        antdMessage.error(err.message || "Fetch error");
        setHistoryData([]);
      } finally {
        setLoading(false);
      }
    },
    [projectId, batchId]
  );

  useEffect(() => {
    fetchChunk({ start: 0, filters: {} });
  }, [fetchChunk]);

  return {
    loading,
    historyData,
    backendMessage,
    statusMessage,
    chunkStart,
    nextChunkStart,
    totalFiltered,
    frontPage,
    setFrontPage,
    fetchChunk,
    requiresServerFiltering,
    lastFiltersKey,
  };
};
